import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkedin, faGithub, faInstagram, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import { faArrowDown, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import Reveal from './Reveal';
import Eyebrow from './Eyebrow';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { contactCopy, identity } from '../data/content';

const THANK_YOU_MESSAGE = "Thanks for submitting — I'll get back to you soon!";

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', reason: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
    } catch (err) {
      // Best-effort submission — the visitor still gets a warm confirmation
      // regardless of backend/DB availability; log for our own debugging only.
      console.error('Contact form submission failed:', err);
    } finally {
      setSubmitMessage(THANK_YOU_MESSAGE);
      setFormData({ name: '', email: '', reason: '' });
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="scroll-mt-20 snap-start border-t border-border py-24 md:py-32">
      <div className="container grid grid-cols-1 gap-12 lg:grid-cols-[1.1fr_1fr] lg:items-start">
        <div>
          <Reveal>
            <Eyebrow>{contactCopy.eyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="text-[clamp(2.4rem,5vw,3.8rem)] font-bold tracking-tight">{contactCopy.heading}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 max-w-[52ch] text-[1.05rem] leading-relaxed text-muted">{contactCopy.sub}</p>
          </Reveal>

          <Reveal delay={0.15} className="mt-8 flex flex-wrap gap-3">
            <Button asChild variant="outline">
              <a href={`mailto:${identity.email}`}>
                <FontAwesomeIcon icon={faEnvelope} /> {identity.email}
              </a>
            </Button>
            <Button asChild variant="outline">
              <a href={identity.resumeUrl} download>
                <FontAwesomeIcon icon={faArrowDown} /> Resume
              </a>
            </Button>
          </Reveal>

          <Reveal delay={0.2} className="mt-8 flex gap-5 text-lg text-muted">
            <a href={identity.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="transition-colors hover:text-primary-light">
              <FontAwesomeIcon icon={faLinkedin} />
            </a>
            <a href={identity.social.github} target="_blank" rel="noopener noreferrer" aria-label="GitHub" className="transition-colors hover:text-primary-light">
              <FontAwesomeIcon icon={faGithub} />
            </a>
            <a href={identity.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="transition-colors hover:text-primary-light">
              <FontAwesomeIcon icon={faInstagram} />
            </a>
            <a href={identity.social.twitter} target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" className="transition-colors hover:text-primary-light">
              <FontAwesomeIcon icon={faXTwitter} />
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <Card className="p-6 md:p-8">
            <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="name" className="sr-only">Your name</label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full rounded-lg border border-border bg-background px-4 py-3 text-[0.98rem] text-foreground placeholder:text-faint focus:border-primary-light focus:outline-none"
                />
              </div>
              <div>
                <label htmlFor="email" className="sr-only">Your email</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  placeholder="Your email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full rounded-lg border border-border bg-background px-4 py-3 text-[0.98rem] text-foreground placeholder:text-faint focus:border-primary-light focus:outline-none"
                />
              </div>
              <div>
                <label htmlFor="reason" className="sr-only">What's on your mind</label>
                <textarea
                  id="reason"
                  name="reason"
                  placeholder="What's on your mind?"
                  rows={4}
                  value={formData.reason}
                  onChange={handleChange}
                  required
                  className="w-full resize-y rounded-lg border border-border bg-background px-4 py-3 text-[0.98rem] text-foreground placeholder:text-faint focus:border-primary-light focus:outline-none"
                />
              </div>
              <Button type="submit" disabled={isSubmitting} className="self-start">
                {isSubmitting ? 'Sending…' : 'Send message'}
              </Button>
              {submitMessage && <p className="text-sm text-primary-light">{submitMessage}</p>}
            </form>
          </Card>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
